import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Envelope } from '@business-assistant/shared';
// Import types from types/core to avoid circular dependency with api
import type {
  Invoice,
  InvoiceLineItem,
  InvoiceFilters,
  Movement,
  MovementFilters,
  AccountCategory,
  AccountCategoryFilters,
  InvoiceStatus,
  MovementDirection,
} from '../types/core';
import * as api from '../api';

/**
 * Accounting State Interface
 *
 * Manages financial data: invoices, movements, and chart of accounts.
 */
export interface AccountingState {
  // Data
  invoices: Invoice[];
  movements: Movement[];
  accountCategories: AccountCategory[];

  // Selected items
  selectedInvoiceId: string | null;
  selectedMovementId: string | null;

  // Filters
  invoiceFilters: InvoiceFilters;
  movementFilters: MovementFilters;
  accountCategoryFilters: AccountCategoryFilters;

  // Stats
  invoiceStats: {
    totalRevenue: number;
    totalPaid: number;
    totalOutstanding: number;
    overdueCount: number;
    overdueAmount: number;
  } | null;
  movementStats: {
    totalIn: number;
    totalOut: number;
    balance: number;
    byCategory: Record<string, { in: number; out: number }>;
  } | null;

  // UI state
  loading: boolean;
  error: string | null;

  // Invoice actions
  loadInvoices: (filters?: InvoiceFilters) => Promise<void>;
  createInvoice: (data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Invoice | null>;
  updateInvoice: (id: string, updates: Partial<Invoice>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  selectInvoice: (id: string | null) => void;
  recordPayment: (id: string, amount: number, date: string, method: string) => Promise<void>;
  setInvoiceFilters: (filters: Partial<InvoiceFilters>) => void;
  loadInvoiceStats: () => Promise<void>;

  // Movement actions
  loadMovements: (filters?: MovementFilters) => Promise<void>;
  createMovement: (data: Omit<Movement, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Movement | null>;
  updateMovement: (id: string, updates: Partial<Movement>) => Promise<void>;
  deleteMovement: (id: string) => Promise<void>;
  selectMovement: (id: string | null) => void;
  setMovementFilters: (filters: Partial<MovementFilters>) => void;
  loadMovementStats: (startDate?: string, endDate?: string) => Promise<void>;

  // Account category actions
  loadAccountCategories: (filters?: AccountCategoryFilters) => Promise<void>;
  createAccountCategory: (data: Omit<AccountCategory, 'id' | 'createdAt' | 'updatedAt'>) => Promise<AccountCategory | null>;
  updateAccountCategory: (id: string, updates: Partial<AccountCategory>) => Promise<void>;
  deleteAccountCategory: (id: string) => Promise<void>;
  setAccountCategoryFilters: (filters: Partial<AccountCategoryFilters>) => void;

  // Utility actions
  clearAllData: () => void;
  seedSampleData: () => Promise<void>;
  clearError: () => void;
}

/**
 * Accounting Zustand Store
 *
 * Manages accounting state with localStorage persistence for UI preferences.
 */
export const useAccountingStore = create<AccountingState>()(
  persist(
    immer((set, get) => ({
      // Initial data
      invoices: [],
      movements: [],
      accountCategories: [],

      // Initial selected items
      selectedInvoiceId: null,
      selectedMovementId: null,

      // Initial filters
      invoiceFilters: {},
      movementFilters: {},
      accountCategoryFilters: { activeOnly: true },

      // Initial stats
      invoiceStats: null,
      movementStats: null,

      // Initial UI state
      loading: false,
      error: null,

      // Invoice actions
      loadInvoices: async (filters?: InvoiceFilters) => {
        set((state) => {
          state.loading = true;
          state.error = null;
          if (filters !== undefined) {
            state.invoiceFilters = filters;
          }
        });

        try {
          const result = await api.listInvoices(filters || get().invoiceFilters);

          if (result.success && result.data) {
            set((state) => {
              state.invoices = result.data!;
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to load invoices';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error loading invoices';
            state.loading = false;
          });
        }
      },

      createInvoice: async (data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.createInvoice(data);

          if (result.success && result.data) {
            set((state) => {
              state.invoices.push(result.data!);
              state.loading = false;
            });
            return result.data;
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to create invoice';
              state.loading = false;
            });
            return null;
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error creating invoice';
            state.loading = false;
          });
          return null;
        }
      },

      updateInvoice: async (id: string, updates: Partial<Invoice>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.updateInvoice(id, updates);

          if (result.success) {
            set((state) => {
              const index = state.invoices.findIndex((inv) => inv.id === id);
              if (index !== -1) {
                state.invoices[index] = { ...state.invoices[index], ...updates };
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to update invoice';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error updating invoice';
            state.loading = false;
          });
        }
      },

      deleteInvoice: async (id: string) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.deleteInvoice(id);

          if (result.success) {
            set((state) => {
              state.invoices = state.invoices.filter((inv) => inv.id !== id);
              if (state.selectedInvoiceId === id) {
                state.selectedInvoiceId = null;
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to delete invoice';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error deleting invoice';
            state.loading = false;
          });
        }
      },

      selectInvoice: (id: string | null) => {
        set((state) => {
          state.selectedInvoiceId = id;
        });
      },

      recordPayment: async (id: string, amount: number, date: string, method: string) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.recordInvoicePayment(id, amount, date, method);

          if (result.success) {
            // Reload invoice to get updated data
            const invoiceResult = await api.getInvoice(id);
            if (invoiceResult.success && invoiceResult.data) {
              set((state) => {
                const index = state.invoices.findIndex((inv) => inv.id === id);
                if (index !== -1) {
                  state.invoices[index] = invoiceResult.data!;
                }
                state.loading = false;
              });
            }
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to record payment';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error recording payment';
            state.loading = false;
          });
        }
      },

      setInvoiceFilters: (filters: Partial<InvoiceFilters>) => {
        set((state) => {
          state.invoiceFilters = { ...state.invoiceFilters, ...filters };
        });
        get().loadInvoices();
      },

      loadInvoiceStats: async () => {
        try {
          const result = await api.getInvoiceStats();

          if (result.success && result.data) {
            set((state) => {
              state.invoiceStats = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load invoice stats:', err);
        }
      },

      // Movement actions
      loadMovements: async (filters?: MovementFilters) => {
        set((state) => {
          state.loading = true;
          state.error = null;
          if (filters !== undefined) {
            state.movementFilters = filters;
          }
        });

        try {
          const result = await api.listMovements(filters || get().movementFilters);

          if (result.success && result.data) {
            set((state) => {
              state.movements = result.data!;
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to load movements';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error loading movements';
            state.loading = false;
          });
        }
      },

      createMovement: async (data: Omit<Movement, 'id' | 'createdAt' | 'updatedAt'>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.createMovement(data);

          if (result.success && result.data) {
            set((state) => {
              state.movements.push(result.data!);
              state.loading = false;
            });
            return result.data;
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to create movement';
              state.loading = false;
            });
            return null;
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error creating movement';
            state.loading = false;
          });
          return null;
        }
      },

      updateMovement: async (id: string, updates: Partial<Movement>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.updateMovement(id, updates);

          if (result.success) {
            set((state) => {
              const index = state.movements.findIndex((mov) => mov.id === id);
              if (index !== -1) {
                state.movements[index] = { ...state.movements[index], ...updates };
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to update movement';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error updating movement';
            state.loading = false;
          });
        }
      },

      deleteMovement: async (id: string) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.deleteMovement(id);

          if (result.success) {
            set((state) => {
              state.movements = state.movements.filter((mov) => mov.id !== id);
              if (state.selectedMovementId === id) {
                state.selectedMovementId = null;
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to delete movement';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error deleting movement';
            state.loading = false;
          });
        }
      },

      selectMovement: (id: string | null) => {
        set((state) => {
          state.selectedMovementId = id;
        });
      },

      setMovementFilters: (filters: Partial<MovementFilters>) => {
        set((state) => {
          state.movementFilters = { ...state.movementFilters, ...filters };
        });
        get().loadMovements();
      },

      loadMovementStats: async (startDate?: string, endDate?: string) => {
        try {
          const result = await api.getMovementStats(startDate, endDate);

          if (result.success && result.data) {
            set((state) => {
              state.movementStats = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load movement stats:', err);
        }
      },

      // Account category actions
      loadAccountCategories: async (filters?: AccountCategoryFilters) => {
        set((state) => {
          state.loading = true;
          state.error = null;
          if (filters !== undefined) {
            state.accountCategoryFilters = filters;
          }
        });

        try {
          const result = await api.listAccountCategories(
            filters || get().accountCategoryFilters
          );

          if (result.success && result.data) {
            set((state) => {
              state.accountCategories = result.data!;
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to load account categories';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Unknown error loading account categories';
            state.loading = false;
          });
        }
      },

      createAccountCategory: async (
        data: Omit<AccountCategory, 'id' | 'createdAt' | 'updatedAt'>
      ) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.createAccountCategory(data);

          if (result.success && result.data) {
            set((state) => {
              state.accountCategories.push(result.data!);
              state.loading = false;
            });
            return result.data;
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to create account category';
              state.loading = false;
            });
            return null;
          }
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Unknown error creating account category';
            state.loading = false;
          });
          return null;
        }
      },

      updateAccountCategory: async (id: string, updates: Partial<AccountCategory>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.updateAccountCategory(id, updates);

          if (result.success) {
            set((state) => {
              const index = state.accountCategories.findIndex((cat) => cat.id === id);
              if (index !== -1) {
                state.accountCategories[index] = {
                  ...state.accountCategories[index],
                  ...updates,
                };
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to update account category';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Unknown error updating account category';
            state.loading = false;
          });
        }
      },

      deleteAccountCategory: async (id: string) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.deleteAccountCategory(id);

          if (result.success) {
            set((state) => {
              state.accountCategories = state.accountCategories.filter(
                (cat) => cat.id !== id
              );
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to delete account category';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Unknown error deleting account category';
            state.loading = false;
          });
        }
      },

      setAccountCategoryFilters: (filters: Partial<AccountCategoryFilters>) => {
        set((state) => {
          state.accountCategoryFilters = {
            ...state.accountCategoryFilters,
            ...filters,
          };
        });
        get().loadAccountCategories();
      },

      // Utility actions
      clearAllData: () => {
        api.clearAllData();
        set((state) => {
          state.invoices = [];
          state.movements = [];
          state.accountCategories = [];
          state.selectedInvoiceId = null;
          state.selectedMovementId = null;
          state.invoiceStats = null;
          state.movementStats = null;
        });
      },

      seedSampleData: async () => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          await api.seedSampleData();
          await get().loadInvoices();
          await get().loadMovements();
          await get().loadAccountCategories();
          await get().loadInvoiceStats();
          await get().loadMovementStats();

          set((state) => {
            state.loading = false;
          });
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error seeding data';
            state.loading = false;
          });
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    })),
    {
      name: 'accounting-storage',
      partialize: (state) => ({
        selectedInvoiceId: state.selectedInvoiceId,
        selectedMovementId: state.selectedMovementId,
        invoiceFilters: state.invoiceFilters,
        movementFilters: state.movementFilters,
        accountCategoryFilters: state.accountCategoryFilters,
      }),
    }
  )
);

// Computed selectors
export const useSelectedInvoice = () => {
  return useAccountingStore((state) => {
    const id = state.selectedInvoiceId;
    return id ? state.invoices.find((inv) => inv.id === id) || null : null;
  });
};

export const useSelectedMovement = () => {
  return useAccountingStore((state) => {
    const id = state.selectedMovementId;
    return id ? state.movements.find((mov) => mov.id === id) || null : null;
  });
};

export const useOverdueInvoices = () => {
  return useAccountingStore((state) => {
    const now = new Date().toISOString();
    return state.invoices.filter(
      (inv) => inv.status !== 'paid' && inv.status !== 'cancelled' && inv.dueDate < now
    );
  });
};

export const useRevenueMovements = () => {
  return useAccountingStore((state) =>
    state.movements.filter((mov) => mov.direction === 'in')
  );
};

export const useExpenseMovements = () => {
  return useAccountingStore((state) =>
    state.movements.filter((mov) => mov.direction === 'out')
  );
};
